import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Button, Card, Table, Alert, Spinner, Form, InputGroup, Modal } from "react-bootstrap";
import { useSuperState } from "helpers/hooks";
import { useApp } from "common/context";
import { Formik, Field, Form as FormikForm } from "formik";

const SessionCreator = forwardRef((props, forwardedRef) => {

    const [state, setState, resetState] = useSuperState({
        error: null,
        url: null,
        show: false
    });

    const { api, session_connector, fleets } = useApp();
    const formRef = useRef();

    const { show, error } = state;

    const handleSubmit = (values, { setSubmitting }) => {

        api.post("/api/lms/launch", { ...values, skip_validation: 1 }).then(({ url }) => {

            if (values.new_window == 1) {
                window.open(url, '_blank');
                location.reload();
            } else {
                session_connector.show(url);
            }

        }).catch(e => {
            setState({ error: e?.err || e.toString() });
        });

        setSubmitting(false);

    };

    const handleConnectionStringBtn = () => {

        let input = window.prompt('Paste the gmetrixsms5: connection string');
        if (!input)
            return;

        // Mapping from command-line flag to object property name
        const flagMapping = {
            u: "user_name",
            p: "user_token",
            a: "practice_test_result_id",
            uid: "uid",
            ctag: "ctag"
        };

        // Regex to match each flag and its corresponding value.
        // This regex looks for a dash (-), then one of the allowed flags,
        // followed by one or more whitespace characters, then a non-space value.
        const regex = /-(u|p|a|uid|ctag)\s+(\S+)/g;

        const params = {};

        // Using String.matchAll (available in Node 12+)
        for (const match of input.matchAll(regex)) {
            const flag = match[1]; // e.g. "u"
            const value = match[2]; // e.g. "myUser"
            const key = flagMapping[flag]; // Map flag to meaningful key name
            params[key] = value;
            if (value)
                formRef.current.setFieldValue(key, value);
        }

    };

    const handleClose = () => {

        setState({
            show: false
        });

    };

    const handleShow = async url => {

        resetState();

        setState({
            show: true
        });

    };

    useImperativeHandle(forwardedRef, () => ({
        show: handleShow
    }));

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Start a Session
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="warning" onClose={() => { setState({ error: null }); }} dismissible>{error}</Alert>}
                {!error &&
                    <Formik
                        initialValues={{
                        user_token: "",
                        practice_test_result_id: "",
                        uid: "",
                        user_name: "",
                        ctag: "en-US",
                        new_window: 1,
                        fleet_name: fleets.length > 0 ? fleets[0].appstream_fleet_name : undefined
                    }}
                        innerRef={formRef}
                        onSubmit={handleSubmit}>
                        {({ isSubmitting }) => (
                            <FormikForm>
                                <Table className="m-0" borderless>
                                    <tbody>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">SMS User Token</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <InputGroup>
                                                    <InputGroup.Text>-p</InputGroup.Text>
                                                    <Field name="user_token" type="text" className="form-control" />
                                                </InputGroup>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">SMS Test Result ID</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <InputGroup>
                                                    <InputGroup.Text>-a</InputGroup.Text>
                                                    <Field name="practice_test_result_id" type="text" className="form-control" />
                                                </InputGroup>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">SMS User ID</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <InputGroup>
                                                    <InputGroup.Text>-uid</InputGroup.Text>
                                                    <Field name="uid" type="text" className="form-control" />
                                                </InputGroup>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">SMS User Name</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <InputGroup>
                                                    <InputGroup.Text>-u</InputGroup.Text>
                                                    <Field name="user_name" type="text" className="form-control" />
                                                </InputGroup>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">SMS Culture Tag</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <InputGroup>
                                                    <InputGroup.Text>-ctag</InputGroup.Text>
                                                    <Field
                                                        name="ctag"
                                                        type="text"
                                                        className="form-control"
                                            />
                                                </InputGroup>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">Target</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <Field name="new_window" as="select" className="form-select">
                                                    <option key={1} value={1}>
                                                        New Window
                                                    </option>
                                                    <option key={0} value={0}>
                                                        Modal
                                                    </option>
                                                </Field>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle">
                                                <Form.Label className="m-0">Fleet Name</Form.Label>
                                            </td>
                                            <td className="align-middle">
                                                <Field name="fleet_name" as="select" className="form-select">
                                                    {fleets.map((fleet, i) => (
                                                        <option key={i} value={fleet.appstream_fleet_name}>{fleet.name}</option>
                                                ))}
                                                </Field>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2}>
                                                <div className="d-flex justify-content-end mt-3">
                                                    <Button variant="outline-primary" type="button" className="me-2" onClick={handleConnectionStringBtn}>
                                                        Connection String
                                                    </Button>
                                                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                                                        Launch
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </FormikForm>
                    )}
                    </Formik>
                }
            </Modal.Body>
        </Modal>
    );
});

export default SessionCreator;
